export interface GuidedImportColumn {
  readonly key: string;
  readonly header: string;
  readonly label: string;
  readonly required: boolean;
  readonly options?: readonly string[];
  readonly requiredForCommit?: boolean;
}
export interface GuidedImportSheet {
  readonly id: string;
  readonly name: string;
  readonly entityClass: string;
  readonly columns: readonly GuidedImportColumn[];
}
export interface GuidedImportPack {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly permitted: boolean;
  readonly commitAvailability: 'ready' | 'review-only';
  readonly nativeWriteAvailable?: boolean;
  readonly reason?: string | null;
  readonly sheets: readonly GuidedImportSheet[];
}

/** Metadata owns the workbook contract. Example values live only in the guide. */
export async function buildGuidedImportTemplate(
  pack: GuidedImportPack,
  maxRows = 500,
): Promise<Blob> {
  if (!pack.permitted || !pack.sheets.length) throw new Error('قالب این بخش در دسترس نیست.');
  const { default: ExcelJS } = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  const used = new Set<string>(['راهنما']);
  for (const sheet of pack.sheets) {
    if (
      !sheet.name ||
      sheet.name.length > 31 ||
      /[\\/?*[\]:]/.test(sheet.name) ||
      used.has(sheet.name) ||
      !sheet.columns.length ||
      new Set(sheet.columns.map((column) => column.header)).size !== sheet.columns.length ||
      sheet.columns.some((column) => !column.key || !column.header)
    )
      throw new Error('تعریف شیت‌های قالب معتبر نیست.');
    used.add(sheet.name);
    const data = workbook.addWorksheet(sheet.name, {
      views: [{ rightToLeft: true, state: 'frozen', ySplit: 1 }],
    });
    data.columns = sheet.columns.map((column) => ({
      header: column.header,
      key: column.key,
      width: 28,
      style: { numFmt: '@' },
    }));
    data.getRow(1).font = { bold: true };
    data.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: sheet.columns.length } };
    sheet.columns.forEach((column, index) => {
      if (column.options?.length) {
        const letter = data.getColumn(index + 1).letter;
        // A range validation does not create placeholder data rows.
        // ExcelJS supports range validation at runtime but omits it from Worksheet typings.
        const validations = (
          data as typeof data & {
            readonly dataValidations: { add(address: string, validation: DataValidation): void };
          }
        ).dataValidations;
        validations.add(`${letter}2:${letter}${maxRows + 1}`, {
          type: 'list',
          allowBlank: !column.required,
          formulae: [`"${column.options.join(',')}"`],
          showErrorMessage: true,
          errorStyle: 'stop',
          errorTitle: 'مقدار نامعتبر',
          error: 'یکی از مقدارهای فهرست را انتخاب کنید.',
        });
      }
      data.getCell(1, index + 1).note =
        `${column.label}\n${column.requiredForCommit ? 'الزامی برای ثبت افتتاحیه؛ مقدار را صریح وارد کنید، مقدار یا تاریخ حدس زده نمی‌شود.' : column.required ? 'الزامی؛ مقدار خالی در ردیف داده باید پیش از ثبت اصلاح شود.' : 'اختیاری؛ یک سلول یا تمام ستون می‌تواند خالی باشد.'}\nعنوان ستون را تغییر ندهید؛ مقدار را به‌صورت متن وارد کنید.${column.options?.length ? `\nمقدارهای مجاز: ${column.options.join('، ')}` : ''}`;
    });
  }
  const guide = workbook.addWorksheet('راهنما', { views: [{ rightToLeft: true }] });
  guide.addRow([pack.label, 'داده‌های خود را از ردیف دوم شیت‌های داده وارد کنید.']);
  guide.addRow([
    'نوع ستون‌ها',
    'همه ستون‌ها متن هستند؛ صفر ابتدای کد، شناسه و شماره تلفن را حفظ کنید.',
  ]);
  guide.addRow([
    'ارتباط ردیف‌ها',
    'برای اشاره به مشتری، تأمین‌کننده یا کالا، کد همان رکورد را در همه شیت‌ها یکسان بنویسید.',
  ]);
  guide.addRow([
    'مثال ارتباط',
    'کد طرف‌حساب P001 در شیت طرف‌حساب؛ همان P001 در ستون کد طرف‌حسابِ ردیف مرتبط.',
  ]);
  guide.addRow(['مثال واحد', 'کد واحد U001 در شیت واحدها؛ همان U001 در ستون کد واحد پایهِ کالا.']);
  guide.addRow([
    'نمونه‌ها',
    'این مثال‌ها فقط راهنما هستند و ردیف قابل ورود به اطلاعات شما نیستند.',
  ]);
  guide.addRow([
    'فیلد اختیاری',
    'یک سلول یا تمام ستون اختیاری می‌تواند خالی باشد؛ عنوان ستون‌ها را تغییر ندهید.',
  ]);
  guide.addRow([
    'مقدار الزامی خالی',
    'ردیف کاملاً خالی وارد نمی‌شود. مقدار الزامی خالی باید پیش از ثبت اصلاح شود؛ مقدار حدس زده نمی‌شود.',
  ]);
  guide.addRow([
    'انتخاب اطلاعات اصلی',
    'برای اتصال طرف‌حساب‌ها، ردیف اصلی را انتخاب می‌کنید. مقدار خالی آن خودکار از منبع دیگر پر نمی‌شود؛ اطلاعات منابع حفظ می‌شوند و می‌توانید ردیف کامل‌تر را انتخاب کنید.',
  ]);
  guide.addRow([
    'بررسی پیش از ثبت',
    'فایل ابتدا بررسی می‌شود؛ ارتباط پیشنهادی طرف‌حساب‌ها فقط با انتخاب شما ثبت می‌شود.',
  ]);
  guide.addRow([
    'وضعیت ورود',
    pack.nativeWriteAvailable
      ? 'داده‌های پایه در جدول اصلی و سفارش‌ها و افتتاحیه در جداول ماژول مربوط با وضعیت پیش‌نویس ثبت می‌شوند؛ Posting خودکار نیست.'
      : pack.commitAvailability === 'ready'
        ? 'ثبت اطلاعات آماده، پس از بررسی و تأیید شما انجام می‌شود.'
        : (pack.reason ?? 'این بخش فعلاً برای بررسی آماده می‌شود؛ ثبت نهایی آن فعال نیست.'),
  ]);
  guide.addRow([]);
  guide.addRow(['شیت', 'عنوان ستون', 'الزام', 'توضیح']);
  for (const sheet of pack.sheets)
    for (const column of sheet.columns)
      guide.addRow([
        sheet.name,
        column.header,
        column.requiredForCommit ? 'الزامی برای ثبت' : column.required ? 'الزامی' : 'اختیاری',
        column.options?.length
          ? `${column.label}؛ مقدارهای مجاز: ${column.options
              .map((value) => {
                const translations: Record<string, string> = {
                  sales: 'فروش',
                  purchase: 'خرید',
                  exclusive: 'قیمت بدون مالیات',
                  inclusive: 'شامل مالیات',
                };
                return translations[value] ? `${value} (${translations[value]})` : value;
              })
              .join('، ')}`
          : column.label,
      ]);
  guide.columns = [{ width: 30 }, { width: 44 }, { width: 20 }, { width: 85 }];
  guide.eachRow((row) => {
    row.alignment = { wrapText: true, vertical: 'top' };
    row.eachCell((cell) => {
      cell.numFmt = '@';
    });
  });
  return new Blob([await workbook.xlsx.writeBuffer()], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}
import type { DataValidation } from 'exceljs';
